import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import CommonCard from '../../components/card/CommonCard';
import Dropdown from '../../components/dropdown';
import { ExerciseDataItem } from './components';
import WeekCalendar from '../../components/charts/donutChart/weeklyCalendar';
import { DidimIcon, CaloryIcon, TimeIcon } from '../../assets/images/activityIcons';
import DonutChart from '../../components/charts/donutChart';
import RadarChart from '../../components/charts/radarChart';
import { EXERCISE_TARGETS, API_PAGE_SIZE } from '../../constants';
import { calculatePercentage, formatDateTime, formatDateTimeDisplay, isWebView, getLocalizedName } from '../../utils/base';
import { logAPI, membershipAPI, contentAPI, recommendationAPI, skillAPI, physicalRecordAPI } from '../../actions/imsServer';
import Recommendation from '../../components/recommendation';
import ActivityCard from '../../components/activityCard';

export const ReportDidim = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // Dropdown 상태 관리
    const [selectedOrganization, setSelectedOrganization] = useState(
        meInfo.mainOrganizationId?.toString() || ''
    );
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    });

    // API 데이터 상태
    const [weeklyPlayLogs, setWeeklyPlayLogs] = useState([]);
    const [playLogs, setPlayLogs] = useState([]);
    const [contents, setContents] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [recommendation, setRecommendation] = useState(null);
    const [, setSkillData] = useState(null);
    const [yesterdayPhysicalRecord, setYesterdayPhysicalRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);

    // 웹뷰로 오늘의 didim/mins 자동 전송
    useEffect(() => {
        if (isWebView() && weeklyPlayLogs.length > 0) {
            const mainOrgId = meInfo.mainOrganizationId;
            const orgLogs = weeklyPlayLogs.filter(log => log.organizationId === mainOrgId);
            const didim = orgLogs.reduce((acc, log) => acc + (log.stepCount || 0), 0);
            const durationSec = orgLogs.reduce((acc, log) => {
                let span = 0;
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    span = Math.max(0, (end - start) / 1000);
                } else if (log.duration) {
                    span = log.duration;
                }
                return acc + span;
            }, 0);
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'playData',
                    data: {
                        didim: didim,
                        mins: (durationSec / 60).toFixed(2),
                        updateTime: new Date().getTime()
                    }
                })
            );
        }
    }, [weeklyPlayLogs, meInfo.mainOrganizationId]);

    const handleOrganizationChange = (item) => {
        setSelectedOrganization(item.value);
    };

    const handleDayClick = (date) => {
        if (date) {
            const newDate = new Date(date);
            newDate.setHours(0, 0, 0, 0);
            setSelectedDate(newDate);
        }
    }

    // 기관 목록 가져오기
    const fetchOrganizations = async () => {
        if (!meInfo.id) return;

        try {
            const response = await membershipAPI.getMemberships({
                userId: meInfo.id,
                type: 1,
                state: 0,
                expand: 'organization'
            });

            if (response?.data?.memberships) {
                const mainOrgId = meInfo.mainOrganizationId?.toString() || '';

                const orgList = response.data.memberships.map(m => ({
                    value: m.organizationId?.toString() || '',
                    label: m.organization?.name || t('no_organization')
                }));

                // main 기관을 맨 위로 정렬
                const sortedOrgList = orgList.sort((a, b) => {
                    const aIsMain = a.value === mainOrgId;
                    const bIsMain = b.value === mainOrgId;
                    if (aIsMain && !bIsMain) return -1;
                    if (!aIsMain && bIsMain) return 1;
                    return 0;
                });

                setOrganizations(sortedOrgList);

                // main 기관이 있으면 기본 선택
                if (!selectedOrganization && mainOrgId) {
                    const mainOrgExists = sortedOrgList.some(org => org.value === mainOrgId);
                    if (mainOrgExists) {
                        setSelectedOrganization(mainOrgId);
                    }
                }
            }
        } catch (err) {
            console.error('기관 목록 로딩 실패:', err);
        }
    };

    // 주간 플레이 로그 데이터 가져오기 (100건씩 배치)
    const fetchWeeklyPlayLogs = useCallback(async (startDate, endDate, isInitialLoad = false) => {
        if (!meInfo.id) return;

        try {
            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const response = await logAPI.getPlayLogs({
                    userId: meInfo.id,
                    organizationId: selectedOrganization || undefined,
                    startTime: startDate.toISOString(),
                    endTime: endDate.toISOString(),
                    expand: 'organization',
                    offset
                });
                page = response?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allLogs.length > 0) {
                // 기존 데이터와 병합 (중복 제거)
                setWeeklyPlayLogs(prevLogs => {
                    const existingIds = new Set(prevLogs.map(log => log.id));
                    const uniqueNewLogs = allLogs.filter(log => !existingIds.has(log.id));
                    return [...prevLogs, ...uniqueNewLogs].sort((a, b) => {
                        const dateA = new Date(a.time || a.startTime);
                        const dateB = new Date(b.time || b.startTime);
                        return dateA - dateB;
                    });
                });
            }
        } catch (err) {
            console.error('주간 플레이 로그 데이터 로딩 실패:', err);
            setError(err.message);
        }
    }, [meInfo.id, selectedOrganization]);

    // 초기 데이터 로드 (컴포넌트 마운트 시)
    useEffect(() => {
        if (!meInfo.id) return;

        const today = new Date();
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 13); // 과거 2주 (14일)
        startDate.setHours(0, 0, 0, 0);

        const endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);

        fetchWeeklyPlayLogs(startDate, endDate, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchWeeklyPlayLogs는 의존성에서 제외
    }, [meInfo.id, selectedOrganization]);

    // 스크롤로 새로운 구간 로드
    const loadMoreData = (direction) => {
        if (weeklyPlayLogs.length === 0) return;

        const sortedLogs = [...weeklyPlayLogs].sort((a, b) => {
            const dateA = new Date(a.time || a.startTime);
            const dateB = new Date(b.time || b.startTime);
            return dateA - dateB;
        });
        const earliestDate = new Date(sortedLogs[0].time || sortedLogs[0].startTime);
        const latestDate = new Date(sortedLogs[sortedLogs.length - 1].time || sortedLogs[sortedLogs.length - 1].startTime);

        if (direction === 'past') {
            // 과거 데이터 더 로드
            const newStartDate = new Date(earliestDate);
            newStartDate.setDate(newStartDate.getDate() - 14);
            newStartDate.setHours(0, 0, 0, 0);
            const newEndDate = new Date(earliestDate);
            newEndDate.setDate(newEndDate.getDate() - 1);
            newEndDate.setHours(23, 59, 59, 999);

            fetchWeeklyPlayLogs(newStartDate, newEndDate, false);
        } else if (direction === 'future') {
            // 미래 데이터 더 로드 (오늘까지만)
            const today = new Date();
            const newStartDate = new Date(latestDate);
            newStartDate.setDate(newStartDate.getDate() + 1);
            newStartDate.setHours(0, 0, 0, 0);
            const newEndDate = new Date(Math.min(today.getTime(), latestDate.getTime() + 14 * 24 * 60 * 60 * 1000));
            newEndDate.setHours(23, 59, 59, 999);

            if (newStartDate <= today) {
                fetchWeeklyPlayLogs(newStartDate, newEndDate, false);
            }
        }
    };

    // 플레이 로그 가져오기 (선택된 날짜의 운동 활동)
    const fetchPlayLogs = useCallback(async () => {
        if (!meInfo.id) return;

        try {
            const selectedDateStart = new Date(selectedDate);
            selectedDateStart.setHours(0, 0, 0, 0);

            const selectedDateEnd = new Date(selectedDate);
            selectedDateEnd.setHours(23, 59, 59, 999);

            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const response = await logAPI.getPlayLogs({
                    userId: meInfo.id,
                    organizationId: selectedOrganization || undefined,
                    startTime: selectedDateStart.toISOString(),
                    endTime: selectedDateEnd.toISOString(),
                    expand: 'organization',
                    offset
                });
                page = response?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            if (allLogs.length > 0) {
                setPlayLogs(allLogs);

                // 콘텐츠 정보 가져오기
                const uniqueContentIndexes = [...new Set(
                    allLogs
                        .map(log => log.contentIndex)
                        .filter(index => index != null)
                )];

                if (uniqueContentIndexes.length > 0) {
                    const contentIndexStr = uniqueContentIndexes.join(',');
                    const contentsResponse = await contentAPI.getContents({
                        index: contentIndexStr,
                        state: 0
                    });

                    if (contentsResponse?.data?.contents) {
                        setContents(contentsResponse.data.contents);
                    } else {
                        setContents([]);
                    }
                } else {
                    setContents([]);
                }
            } else {
                setPlayLogs([]);
                setContents([]);
            }
        } catch (err) {
            console.error('플레이 로그 데이터 로딩 실패:', err);
            setError(err.message);
            setPlayLogs([]);
            setContents([]);
        }
    }, [meInfo.id, selectedOrganization, selectedDate]);

    // 체력 지표 데이터 가져오기
    const fetchSkill = useCallback(async () => {
        if (!meInfo.id) return;

        try {
            const response = await skillAPI.getSkills({
                userId: meInfo.id,
                organizationId: selectedOrganization || undefined
            });

            if (response?.data?.skills) {
                setSkillData(response.data.skills);
            } else {
                setSkillData(null);
            }
        } catch (err) {
            console.error('스킬 데이터 로드 실패:', err);
            setSkillData(null);
        }
    }, [meInfo.id, selectedOrganization]);

    // 어제 날짜의 physicalRecord 가져오기
    const fetchYesterdayPhysicalRecord = useCallback(async () => {
        if (!meInfo.id) return;

        try {
            // 로컬 시간대의 어제 날짜 계산
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const year = yesterday.getFullYear();
            const month = String(yesterday.getMonth() + 1).padStart(2, '0');
            const day = String(yesterday.getDate()).padStart(2, '0');
            const yesterdayStr = `${year}-${month}-${day}`;

            const response = await physicalRecordAPI.getPhysicalRecords({
                userId: meInfo.id,
                state: 0,
                startDate: yesterdayStr,
                endDate: yesterdayStr,
                limit: 1
            });
            const record = response?.data?.physicalRecords?.[0] ?? null;
            setYesterdayPhysicalRecord(record);
        } catch (err) {
            console.error('어제 physicalRecord 로딩 실패:', err);
            setYesterdayPhysicalRecord(null);
        }
    }, [meInfo.id]);

    // 추천 데이터 가져오기
    const fetchRecommendation = useCallback(async () => {
        if (!meInfo.id) return;

        try {
            const fetchContentsBatched = async () => {
                let all = [];
                let offset = 0;
                let page;
                do {
                    const res = await contentAPI.getContents({ state: 0, offset });
                    page = res?.data?.contents || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };

            const [recommendationResponse, contents] = await Promise.all([
                recommendationAPI.getRecommendations({ userId: meInfo.id }),
                fetchContentsBatched()
            ]);

            // AI 추천 데이터 처리
            const fitnessData = recommendationResponse?.data?.recommendations?.[0]?.fitness;
            if (fitnessData && Array.isArray(fitnessData)) {
                // contentIndex로 contents 매핑
                const contentMap = new Map((contents || []).map(c => [c.index, c]));
                const processedRecommendation = fitnessData.map(item => {
                    const content = contentMap.get(item.contentIndex);
                    return {
                        contentIndex: item.contentIndex,
                        name: (content && getLocalizedName(content, i18n.language)) || content?.name || t('exercise'),
                        icon: content?.icon || null,
                        circuit: item.circuit || '',
                        comment: item.comment || '',
                    };
                });
                setRecommendation(processedRecommendation);
            } else if (fitnessData) {
                // 기존 형식 (하위 호환성)
                setRecommendation(fitnessData);
            }
        } catch (err) {
            console.error('추천 데이터 로딩 실패:', err);
        }
    }, [meInfo.id, t]);

    // 데이터 로드
    useEffect(() => {
        if (!meInfo.id) return;

        const loadData = async () => {
            setLoading(true);
            setError(null);

            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 89); // 과거 3개월 (90일) - 더 많은 데이터 로드
            startDate.setHours(0, 0, 0, 0);

            const endDate = new Date(today);
            endDate.setHours(23, 59, 59, 999);

            await Promise.all([
                fetchOrganizations(),
                fetchWeeklyPlayLogs(startDate, endDate, true),
                fetchPlayLogs(),
                fetchSkill(),
                fetchRecommendation(),
                fetchYesterdayPhysicalRecord()
            ]);

            setLoading(false);
        };

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch* 함수 의존성 제외(반복 호출 방지)
    }, [meInfo.id, selectedOrganization]);

    // 선택된 날짜 변경 시 플레이 로그 다시 로드
    useEffect(() => {
        if (!meInfo.id) return;
        fetchPlayLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedDate 변경 시에만 재실행
    }, [meInfo.id, selectedDate]);

    // Generate this week's dates dynamically with API data
    const generateThisWeek = () => {
        // 선택된 날짜를 기반으로 주간 계산
        const currentDay = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

        // Get start of week (Sunday) based on selectedDate
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(selectedDate.getDate() - currentDay);
        startOfWeek.setHours(0, 0, 0, 0);

        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];
        const week = [];

        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            date.setHours(0, 0, 0, 0);

            const isSelected =
                date.getFullYear() === selectedDate.getFullYear() &&
                date.getMonth() === selectedDate.getMonth() &&
                date.getDate() === selectedDate.getDate();

            // 해당 날짜의 플레이 로그 찾기 및 집계
            const dateStr = formatDateTime(date, '-', ':', 0, 10);
            const dayLogs = weeklyPlayLogs.filter(log => {
                const logDate = new Date(log.time || log.startTime);
                const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
                return logDateStr === dateStr;
            });

            // 날짜별 집계 계산
            const didimValue = dayLogs.reduce((sum, log) => sum + (log.stepCount || 0), 0);
            const caloriesValue = dayLogs.reduce((sum, log) => sum + (log.calories || 0), 0);
            const timeValue = dayLogs.reduce((sum, log) => {
                // startTime과 endTime의 차이로 계산 (초 단위)
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    return sum + Math.floor((end - start) / 1000);
                }
                return sum + (log.duration || 0);
            }, 0); // 초 단위

            const chartData = [
                {
                    value: calculatePercentage(didimValue || 0, EXERCISE_TARGETS.STEPS),
                    color: theme.palette.figma.didim,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    value: calculatePercentage(caloriesValue || 0, EXERCISE_TARGETS.CALORIES),
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    value: calculatePercentage(timeValue || 0, EXERCISE_TARGETS.ACTIVITY_TIME * 60), // 초를 분으로 변환
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];

            week.push({
                day: dayNames[i],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
            });
        }

        return week;
    };

    const days = useMemo(() => generateThisWeek(), [selectedDate, weeklyPlayLogs, theme, t, generateThisWeek]);

    // WeekCalendar용 전체 날짜 데이터 생성 (1년치 고정)
    const allDaysData = useMemo(() => {
        const data = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];

        const earliestDate = new Date(today);
        earliestDate.setDate(today.getDate() - 364); // 1년 전 (365일)
        earliestDate.setHours(0, 0, 0, 0);
        const totalDays = 365;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(earliestDate);
            date.setDate(earliestDate.getDate() + i);
            date.setHours(0, 0, 0, 0);

            const isSelected =
                date.getFullYear() === selectedDate.getFullYear() &&
                date.getMonth() === selectedDate.getMonth() &&
                date.getDate() === selectedDate.getDate();

            // 해당 날짜의 플레이 로그 찾기 및 집계
            const dateStr = formatDateTime(date, '-', ':', 0, 10);
            const dayLogs = weeklyPlayLogs.filter(log => {
                const logDate = new Date(log.time || log.startTime);
                logDate.setHours(0, 0, 0, 0);
                const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
                return logDateStr === dateStr;
            });

            // 날짜별 집계 계산
            const didimValue = dayLogs.reduce((sum, log) => sum + (log.stepCount || 0), 0);
            const caloriesValue = dayLogs.reduce((sum, log) => sum + (log.calories || 0), 0);
            const timeValue = dayLogs.reduce((sum, log) => {
                // startTime과 endTime의 차이로 계산 (초 단위)
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    return sum + Math.floor((end - start) / 1000);
                }
                return sum + (log.duration || 0);
            }, 0); // 초 단위

            const chartData = [
                {
                    value: calculatePercentage(didimValue || 0, EXERCISE_TARGETS.STEPS),
                    color: theme.palette.figma.didim,
                    radius: 15,
                    strokeWidth: 3,
                },
                {
                    value: calculatePercentage(caloriesValue || 0, EXERCISE_TARGETS.CALORIES),
                    color: theme.palette.figma.calorieRed,
                    radius: 12,
                    strokeWidth: 3,
                },
                {
                    value: calculatePercentage(timeValue || 0, EXERCISE_TARGETS.ACTIVITY_TIME * 60), // 초를 분으로 변환
                    color: theme.palette.figma.time,
                    radius: 9,
                    strokeWidth: 3,
                },
            ];

            data.push({
                day: dayNames[date.getDay()],
                date: date,
                isSelected: isSelected,
                chartData: chartData,
            });
        }

        return data;
    }, [weeklyPlayLogs, selectedDate, theme, t]);


    // 선택된 날짜의 플레이 로그 집계
    const selectedDateRecord = useMemo(() => {
        const dateStr = formatDateTime(selectedDate, '-', ':', 0, 10);
        const dayLogs = playLogs.filter(log => {
            const logDate = new Date(log.time || log.startTime);
            const logDateStr = formatDateTime(logDate, '-', ':', 0, 10);
            return logDateStr === dateStr;
        });

        // 집계 계산
        return {
            totalSteps: dayLogs.reduce((sum, log) => sum + (log.stepCount || 0), 0),
            calories: dayLogs.reduce((sum, log) => sum + (log.calories || 0), 0),
            duration: dayLogs.reduce((sum, log) => {
                // startTime과 endTime의 차이로 계산 (초 단위)
                if (log.startTime && log.endTime) {
                    const start = new Date(log.startTime);
                    const end = new Date(log.endTime);
                    return sum + Math.floor((end - start) / 1000);
                }
                return sum + (log.duration || 0);
            }, 0) // 초 단위
        };
    }, [playLogs, selectedDate]);

    // 레이더 차트 데이터
    // 해당 날짜의 playLogs의 contentIndex와 맞는 contents 데이터에서 체력 지표 합산
    const radarData = useMemo(() => {
        // selectedDate의 시간 부분을 제거하여 날짜만 비교
        const selectedDateForCompare = new Date(selectedDate);
        selectedDateForCompare.setHours(0, 0, 0, 0);
        const dateStr = formatDateTime(selectedDateForCompare, '-', ':', 0, 10); // YYYY-MM-DD 형식

        // 선택된 날짜의 playLogs 필터링 (시간 부분 제거하여 날짜만 비교)
        const dayRecords = playLogs?.filter(record => {
            if (!record.time && !record.startTime) return false;
            const recordDate = new Date(record.time || record.startTime);
            recordDate.setHours(0, 0, 0, 0);
            const recordDateStr = formatDateTime(recordDate, '-', ':', 0, 10);
            return recordDateStr === dateStr;
        }) || [];

        if (dayRecords.length > 0 && contents.length > 0) {
            // dayRecords에서 contentIndex 추출
            const contentIndexes = dayRecords
                .map(record => record.contentIndex)
                .filter(index => index != null);

            // 해당 contentIndex와 맞는 contents 데이터 찾기
            const matchedContents = contents.filter(content =>
                contentIndexes.includes(content.index)
            );

            if (matchedContents.length > 0) {
                // 각 콘텐츠의 체력 지표 합산
                const skillTotals = matchedContents.reduce((acc, content) => {
                    return {
                        endurance: acc.endurance + (content.endurance || 0),
                        strength: acc.strength + (content.strength || 0),
                        balance: acc.balance + (content.balance || 0),
                        flexibility: acc.flexibility + (content.flexibility || 0),
                        reaction: acc.reaction + (content.reaction || 0),
                        cooperation: acc.cooperation + (content.cooperation || 0),
                    };
                }, {
                    endurance: 0,
                    strength: 0,
                    balance: 0,
                    flexibility: 0,
                    reaction: 0,
                    cooperation: 0,
                });

                // 합산된 값 반환 (Radar chart에 표시됨)
                return {
                    endurance: skillTotals.endurance,
                    strength: skillTotals.strength,
                    balance: skillTotals.balance,
                    flexibility: skillTotals.flexibility,
                    reaction: skillTotals.reaction,
                    cooperation: skillTotals.cooperation,
                };
            }
        }

        // 데이터가 없으면 기본값 반환
        return {
            endurance: 0,
            strength: 0,
            balance: 0,
            flexibility: 0,
            reaction: 0,
            cooperation: 0,
        };
    }, [selectedDate, playLogs, contents]);


    return (
        <ContentArea sx={{ paddingBottom: '100px' }}>
            {/* 카드/콘텐츠 영역만 좌우 여백 */}
            <Box sx={{ width: '100%', px: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Organization Dropdown */}
                <Dropdown
                    options={organizations}
                    value={selectedOrganization}
                    onChange={handleOrganizationChange}
                    placeholder={t('select_organization')}
                    sx={{
                        marginRight: '10px',
                        alignSelf: 'flex-end'
                    }}
                />
            </Box>

            {/* 주간 달력 (스크롤 가능) - 화면 좌우 100% */}
            <WeekCalendar
                days={days}
                onDayClick={handleDayClick}
                enableScroll={true}
                fullWidth={true}
                allDaysData={allDaysData}
                isLoading={false}
                size="medium"
                selectedDate={selectedDate}
                onScrollToEnd={(direction) => {
                    loadMoreData(direction);
                }}
            />

            {/* 카드 영역만 좌우 여백 */}
            <Box sx={{ display: 'flex', px: '10px', flexDirection: 'column', gap: '10px' }}>
                {/* 일일 요약 카드 */}
                <CommonCard>
                    {/* 날짜 헤더 (선택 언어별 표기) */}
                    <Typography sx={{
                        color: theme.palette.figma.white[80],
                        fontSize: '16px',
                        fontWeight: 500,
                        letterSpacing: '-0.48px',
                        lineHeight: 1,
                        textAlign: 'left',
                        alignSelf: 'stretch'
                    }}>
                        {(() => {
                            const today = new Date();
                            const isToday = selectedDate.getFullYear() === today.getFullYear() &&
                                selectedDate.getMonth() === today.getMonth() &&
                                selectedDate.getDate() === today.getDate();
                            const dateLabel = formatDateTimeDisplay(selectedDate, 'monthDay');
                            return isToday ? `${t('today')} ${dateLabel}` : dateLabel;
                        })()}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%'
                    }}>
                        {/* 메인 통계 */}
                        <Box sx={{
                            display: 'flex',
                            gap: '20px',
                            alignItems: 'center',
                            width: '100%'
                        }}>
                            {/* 왼쪽 통계 */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                flex: 1
                            }}>
                                {/* 디딤 */}
                                <ExerciseDataItem
                                    icon={DidimIcon}
                                    value={selectedDateRecord?.totalSteps || 0}
                                    unit={t('didim')}
                                />

                                {/* Calories */}
                                <ExerciseDataItem
                                    icon={CaloryIcon}
                                    value={selectedDateRecord?.calories || 0}
                                    unit={`kcal`}
                                />

                                {/* Time */}
                                <ExerciseDataItem
                                    icon={TimeIcon}
                                    value={Math.floor((selectedDateRecord?.duration || 0) / 60)}
                                    unit={t('min')}
                                    value2={(selectedDateRecord?.duration || 0) % 60}
                                    unit2={t('sec')}
                                />
                            </Box>

                            {/* 오른쪽 차트 영역 */}
                            <Box sx={{ width: '100px', height: '100px' }}>
                                <DonutChart data={[
                                    {
                                        label: t('didim'),
                                        value: calculatePercentage(
                                            selectedDateRecord?.totalSteps || 0,
                                            EXERCISE_TARGETS.STEPS
                                        ),
                                        color: theme.palette.figma.didim,
                                        radius: 45,
                                        strokeWidth: 10,
                                    },
                                    {
                                        label: t('calories'),
                                        value: calculatePercentage(
                                            selectedDateRecord?.calories || 0,
                                            EXERCISE_TARGETS.CALORIES
                                        ),
                                        color: theme.palette.figma.calorieRed,
                                        radius: 35,
                                        strokeWidth: 10,
                                    },
                                    {
                                        label: t('time'),
                                        value: calculatePercentage(
                                            selectedDateRecord?.duration || 0,
                                            EXERCISE_TARGETS.ACTIVITY_TIME * 60
                                        ),
                                        color: theme.palette.figma.time,
                                        radius: 25,
                                        strokeWidth: 10,
                                    },
                                ]} size={100} centerRadius={60} />
                            </Box>
                        </Box>
                    </Box>
                </CommonCard>

                {/* 운동 활동 리스트 */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    width: '100%'
                }}>
                    {loading ? (
                        <Typography sx={{ color: theme.palette.figma.white[60], textAlign: 'center', padding: '20px' }}>
                            {t('loading')}
                        </Typography>
                    ) : playLogs.length === 0 ? (
                        <Typography sx={{ color: theme.palette.figma.white[60], textAlign: 'center', padding: '20px' }}>
                            {t('no_exercise_records')}
                        </Typography>
                    ) : (
                        playLogs.map((log, index) => {
                            const logDate = new Date(log.time || log.startTime);
                            const timeDisplay = formatDateTimeDisplay(logDate, 'time');

                            // 시간 계산 (startTime과 endTime의 차이, 초를 mm:ss 형식으로)
                            let duration = 0;
                            if (log.startTime && log.endTime) {
                                const start = new Date(log.startTime);
                                const end = new Date(log.endTime);
                                duration = Math.floor((end - start) / 1000); // 초 단위
                            } else {
                                duration = log.duration || 0;
                            }
                            const minutes = Math.floor(duration / 60);
                            const seconds = duration % 60;
                            const timeValue = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

                            // 콘텐츠 정보 찾기
                            const content = contents.find(c => c.index === log.contentIndex);

                            // 콘텐츠 이름 또는 기본값 (선택 언어 반영)
                            const contentName = (content && getLocalizedName(content, i18n.language)) || content?.name || log.gameName || log.contentName || t('exercise_activity');

                            return (
                                <ActivityCard
                                    key={log.id || index}
                                    icon={content?.icon}
                                    title={contentName}
                                    time={timeDisplay}
                                    score={log.score ? `${log.score}${t('pts')}` : undefined}
                                    didimValue={log.stepCount || 0}
                                    calorieValue={log.calories || 0}
                                    timeValue={timeValue}
                                />
                            );
                        })
                    )}
                </Box>

                {/* 체력 레이더 차트 카드 */}
                {playLogs.length > 0 && <CommonCard>
                    {/* Physical Fitness Chart */}
                    <RadarChart
                        data={{
                            endurance: radarData.endurance, // 심폐지구력
                            strength: radarData.strength, // 근력근지구력
                            balance: radarData.balance, // 평형성
                            flexibility: radarData.flexibility, // 유연성
                            reaction: radarData.reaction, // 순발력
                            cooperation: radarData.cooperation, // 협응력
                        }}
                        size={100}
                        showLabels={true}
                        showBackground={true}
                        sx={{ width: 140, height: 160 }}
                    />
                </CommonCard>}

                {/* AI Exercise Recommendation Card */}
                {recommendation && (
                    <Recommendation
                        data={recommendation}
                        userName={meInfo?.name || ''}
                        yesterdaySteps={yesterdayPhysicalRecord?.totalSteps || null}
                        avgHeartRate={yesterdayPhysicalRecord?.avgHeartRate || null}
                    />
                )}
            </Box>
        </ContentArea>
    );
};

export default ReportDidim;